import { Check, CheckCheck, Eye, AlertCircle, AlertTriangle, Clock } from 'lucide-react';

/**
 * Small status pill rendered next to an outbound (`direction === "sent"`)
 * email row. Reflects Resend's delivery lifecycle:
 *
 *   queued/sent → delivered → opened
 *                          ↘ bounced / complained / failed
 *
 * Inbound rows render nothing — engagement only makes sense for emails the
 * user sent. The component is intentionally compact (one icon + one short
 * label) so it slots into dense email cards without breaking layout.
 *
 * Props:
 *   msg        — the email row from /api/claim-emails
 *   className  — optional override (e.g. larger size in detail view)
 */
export const DeliveryPill = ({ msg, className = '' }) => {
  if (!msg || msg.direction === 'received') return null;

  // Order matters: pick the most-terminal status that's present. A bounced
  // email that was also "delivered" (unlikely but possible) should still show
  // as bounced — the user needs to know.
  const status = msg.delivery_status || msg.status;
  const opened = !!msg.opened_at;
  const opensCount = msg.open_count || 0;

  let label, Icon, color, testid;
  if (status === 'bounced') {
    label = 'Bounced'; Icon = AlertTriangle;
    color = 'bg-red-50 text-red-700 border-red-200';
    testid = 'pill-bounced';
  } else if (status === 'complained') {
    label = 'Spam'; Icon = AlertCircle;
    color = 'bg-orange-50 text-orange-700 border-orange-200';
    testid = 'pill-complained';
  } else if (status === 'failed' || msg.status === 'failed') {
    label = 'Failed'; Icon = AlertCircle;
    color = 'bg-red-50 text-red-700 border-red-200';
    testid = 'pill-failed';
  } else if (opened) {
    label = opensCount > 1 ? `Opened ${opensCount}×` : 'Opened';
    Icon = Eye;
    color = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    testid = 'pill-opened';
  } else if (status === 'delivered' || msg.delivered_at) {
    label = 'Delivered'; Icon = CheckCheck;
    color = 'bg-sky-50 text-sky-700 border-sky-200';
    testid = 'pill-delivered';
  } else if (msg.status === 'sent') {
    label = 'Sent'; Icon = Check;
    color = 'bg-gray-50 text-gray-600 border-gray-200';
    testid = 'pill-sent';
  } else if (msg.status === 'queued') {
    label = 'Queued'; Icon = Clock;
    color = 'bg-gray-50 text-gray-500 border-gray-200';
    testid = 'pill-queued';
  } else {
    return null;
  }

  const tooltipParts = [];
  if (msg.delivered_at) tooltipParts.push(`Delivered ${new Date(msg.delivered_at).toLocaleString()}`);
  if (msg.opened_at) tooltipParts.push(`Last opened ${new Date(msg.opened_at).toLocaleString()}`);
  if (msg.bounce_reason) tooltipParts.push(`Bounce: ${msg.bounce_reason}`);
  const tooltip = tooltipParts.join(' · ');

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide border rounded ${color} ${className}`}
      title={tooltip || label}
      data-testid={testid}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

export default DeliveryPill;
